import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import type { OfferPageFilterType, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import OfferCapacityWithAgesSelection from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import OfferAirportOptions from '../Filters/Airport/OfferAirportOptions'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OfferCalendarSidebarDatePicker from '../Filters/CalendarSidebarPicker/OfferCalendarSidebarDatePicker'
import styled from 'styled-components'
import { rem } from 'polished'
import useHolidaySynonymLabels from 'hooks/useHolidaySynonymLabels'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import FlashHotelRoomSelectionModal from './FlashHotelMobileVariant/Modals/FlashHotelRoomSelectionModal'
import FlashHotelPackageSelectionModalWithCarousel from './FlashHotelMobileVariant/Modals/FlashHotelPackageSelectionModalWithCarousel'
import type { OfferDurationOption } from 'lib/offer/offerDurationTypes'
import { getMultiRoomDurationOptions } from 'selectors/offerPage/multipleRoomBookingSelectors'
import OfferDurationOptions from '../Filters/Duration/OfferDurationOptions'
import { min } from 'lib/array/arrayUtils'
import FlashHotelRoomAndPackageDetailsModal from './FlashHotelMobileVariant/Modals/FlashHotelRoomAndPackageDetailsModal'
import { trackClientEvent } from 'analytics/analytics'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import MobileMultipleRoomsBookingSelectRoomsModal from './FlashHotelMobileVariant/Modals/MobileMultipleRoomsBookingSelectRoomsModal'
import CharterCalendarFilter from '../Filters/CharterFlight/CharterCalendarFilter'
import type { FlightsClassTypes } from 'constants/flight'
import { useIntl } from 'react-intl'
import { mobileRoomTestCalendarFilters } from './FlashHotelFilters.constants'

const mapFilterToAnalytics: Record<string, string> = {
  capacity: 'guests',
  duration: 'number_of_nights',
  roomSelection: 'select_room',
  packageSelection: 'select_package',
  roomDetails: 'room_details',
}

const FiltersBlock = styled.div`
  flex-grow: 1;

  > * {
    height: ${rem(64)};
    width: 100%;
    padding: 0 ${rem(24)};

    &:not(:first-child):not(:last-child) {
      box-shadow: 0 -1px 0px 0px ${props => props.theme.palette.neutral.surface.lightForeground};
    }
  }
`

interface Props {
  className?: string
  activeFilter?: OfferPageFilterType
  capacities?: Array<App.RoomCapacity>
  offer: App.Offer
  durationOptions: Array<OfferDurationOption>
  filterHistory: Array<OfferPageFilterType>
  charterBundleRoute?: App.CharterBundleRoute
  // Charter calendar props
  fareClass: FlightsClassTypes
  priceMode: OfferPagePriceMode
  rooms: Array<OfferPageRoom>
  duration: number
  checkInDate?: string
  airport?: App.Airport
}

function FlashHotelSidebarFilters(props: Props) {
  const {
    activeFilter,
    capacities,
    offer,
    durationOptions,
    filterHistory,
    charterBundleRoute,
    fareClass,
    priceMode,
    rooms,
    duration,
    checkInDate,
    airport,
  } = props

  const intl = useIntl()
  const { yourHoliday } = useHolidaySynonymLabels()
  const isMobile = useScreenSizeOnly('mobile')
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const formRef = useRef<HTMLFormElement>(null)
  const hasPreviousFilter = useMemo(() => filterHistory.length > 0, [filterHistory])

  const closeFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
  }, [dispatch, onUserAnalyticsAction])

  const filterContinue = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterContinue })
    }
  }, [dispatch])

  const onBack = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterBack })
    }
  }, [dispatch])

  const onApply = useCallback(() => {
    filterContinue()
  }, [filterContinue])

  const onBackHandler = useMemo(() => (
    hasPreviousFilter ? onBack : undefined
  ), [hasPreviousFilter, onBack])

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })
  const minimumDuration = useMemo(() => min(durationOptions, option => option.duration)?.duration ?? 1, [durationOptions])

  useEffect(() => {
    const clientEventSubject = activeFilter ? mapFilterToAnalytics[activeFilter] : undefined
    if (clientEventSubject) {
      trackClientEvent({
        subject: clientEventSubject,
        action: 'appear',
        type: 'interaction',
        category: 'offer_funnel',
      })
    }
  }, [activeFilter])

  const minimumStaySubtitle = minimumDuration > 1 ? intl.formatMessage(
    {
      id: 'g61vB6MzYpYhWVSxDNW5',
      defaultMessage: 'Minimum stay for this offer is {count, plural, one {# night} other {# nights}}',
    },
    { count: minimumDuration },
  ) : undefined

  return (
    <ModalBase
      isOpen={delayedOpen && isMobile}
      onClose={closeFilter}
      mode="drawer"
    >
      <form onSubmit={onApply} ref={formRef}>
        {activeFilter === 'all' && <>
          <ModalHeader
            title={yourHoliday}
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <FiltersBlock>
              <OfferCapacityWithAgesSelectBox inSidebar />
              <OfferCalendarDatePickerSelectBox inSidebar />
              <OfferAirportSelectBox inSidebar />
            </FiltersBlock>
          </ModalBody>
        </>}
        {activeFilter === 'airport' && <>
          <ModalHeader
            title={intl.formatMessage({
              id: 'VJ4qmaVnvuO2m0boYDnd',
              defaultMessage: 'Where will you depart from?',
            })}
            subtitle={intl.formatMessage({
              id: 'R7fSv-TbhgVF_QrgkE8n',
              defaultMessage: 'Select your preferred airport',
            })}
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <ModalContent>
              <OfferAirportOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: 'JgsoZ9snJ00E-XsdDzwE',
                defaultMessage: 'Apply',
              }),
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'capacity' && <>
          <ModalHeader
            title={intl.formatMessage({
              id: 'YuLp6DNzy4kGL_7Wt_Tx',
              defaultMessage: 'Who is going?',
            })}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBackHandler}
          />
          <ModalBody>
            <OfferCapacityWithAgesSelection
              capacities={capacities}
              disableMultiRoom={isFlightForceBundled(offer)}
            />
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: 'YzUbcTtq8AiLbzx6cx-3',
                defaultMessage: 'Continue',
              }),
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'checkin' && !charterBundleRoute && <OfferCalendarSidebarDatePicker
          onClose={closeFilter}
          onBack={onBackHandler}
          subFilters={mobileRoomTestCalendarFilters}
        />}
        {activeFilter === 'checkin' && charterBundleRoute && <CharterCalendarFilter
          offer={offer as App.HotelOffer}
          occupancies={rooms}
          duration={duration}
          durationOptions={durationOptions}
          priceMode={priceMode}
          onPriceModeChange={(nextPriceMode) => dispatch({ type: OfferPageActions.setPriceMode, priceMode: nextPriceMode })}
          fareClass={fareClass}
          charterBundleRoute={charterBundleRoute}
          checkInDate={checkInDate}
          onDurationChange={(nights) => dispatch({ type: OfferPageActions.setDuration, duration: nights })}
          onClose={closeFilter}
          onBack={onBackHandler}
          onDateSelected={(option) => {
            dispatch({ type: OfferPageActions.setDates, checkIn: option.checkIn, checkOut: option.checkOut })
            closeFilter()
          }}
          onFareClassChange={(newFareClass) => dispatch({ type: OfferPageActions.setFareClass, fareClass: newFareClass })}
          feederOriginAirportCode={airport?.code}
        />}
        {activeFilter === 'roomDetails' && <FlashHotelRoomAndPackageDetailsModal
          onClose={closeFilter}
        />}
        {activeFilter === 'roomSelection' && <FlashHotelRoomSelectionModal
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {activeFilter === 'packageSelection' && <FlashHotelPackageSelectionModalWithCarousel
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {(activeFilter === 'multiRoomSelect' || activeFilter === 'multiRoomConfirm') && <MobileMultipleRoomsBookingSelectRoomsModal
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {activeFilter === 'duration' && <>
          <ModalHeader
            title={intl.formatMessage({
              id: 'el1tqp-0X8N5d5SslkxQ',
              defaultMessage: 'How long do you want to stay?',
            })}
            subtitle={minimumStaySubtitle}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBackHandler}
          />
          <ModalBody>
            <ModalContent>
              <OfferDurationOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: 'YzUbcTtq8AiLbzx6cx-3',
                defaultMessage: 'Continue',
              }),
              type: 'submit',
            }}
          />
        </>}
      </form>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    activeFilter: state.activeFilter,
    offer: state.offer,
    open: !!state.activeFilter,
    durationOptions: isMultiRoom ? getMultiRoomDurationOptions(state, appState) : getDurationOptions(state, appState),
    filterHistory: state.filterHistory,
    charterBundleRoute: state.charterBundleRoute,
    // Charter calendar props
    fareClass: state.fareClass,
    priceMode: state.priceMode,
    rooms: state.rooms,
    duration: state.duration,
    checkInDate: state.checkInDate,
    airport: state.airport,
  }
}, FlashHotelSidebarFilters)
